import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import WorkCard from "@/components/feature/WorkCard";
import Pagination from "@/components/feature/Pagination";
import { Breadcrumb } from "@/components/feature/Breadcrumb";
import FilterPanel from "./components/FilterPanel";
import { allWorks } from "@/mocks/works";
import { categories, regions, yearFilters, ratingFilters } from "@/mocks/categories";

const PAGE_SIZE = 6;

type SortKey = "newest" | "hot" | "rating";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "newest", label: "最新" },
  { key: "hot", label: "最热" },
  { key: "rating", label: "评分" },
];

const filterGroups = [
  { key: "region", title: "地区", options: regions.filter((item) => item !== "全部") },
  { key: "year", title: "年份", options: yearFilters.filter((item) => item !== "全部") },
  { key: "rating", title: "评分", options: ratingFilters.filter((item) => item !== "全部") },
];

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const category = categories.find((item) => item.key === type);

  const [selected, setSelected] = useState<Record<string, string[]>>({
    region: [],
    year: [],
    rating: [],
  });
  const [sort, setSort] = useState<SortKey>("newest");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const baseList = useMemo(
    () => allWorks.filter((work) => work.type === type),
    [type],
  );

  const filteredList = useMemo(() => {
    const list = baseList.filter((work) => {
      const regionSel = selected.region ?? [];
      if (regionSel.length > 0 && !regionSel.includes(work.region)) return false;

      const yearSel = selected.year ?? [];
      if (yearSel.length > 0) {
        const matched = yearSel.some((value) =>
          value === "2021及更早" ? work.year <= 2021 : String(work.year) === value,
        );
        if (!matched) return false;
      }

      const ratingSel = selected.rating ?? [];
      if (ratingSel.length > 0) {
        const matched = ratingSel.some((value) => {
          if (value === "9分以上") return work.rating >= 9;
          if (value === "8分以上") return work.rating >= 8;
          if (value === "7分以上") return work.rating >= 7;
          return work.rating < 7;
        });
        if (!matched) return false;
      }

      return true;
    });

    return list.sort((a, b) => {
      if (sort === "hot") return b.views - a.views;
      if (sort === "rating") return b.rating - a.rating;
      return b.year - a.year;
    });
  }, [baseList, selected, sort]);

  const totalPages = Math.max(1, Math.ceil(filteredList.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedList = filteredList.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [selected, sort, type]);

  useEffect(() => {
    if (!category) {
      document.title = "分类不存在 - 爱妻艺";
      return;
    }
    document.title = `${category.name} - 爱妻艺影视推荐`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", category.description);
  }, [category]);

  const handleToggle = (key: string, value: string) => {
    setSelected((prev) => {
      const current = prev[key] ?? [];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [key]: next };
    });
  };

  const handleReset = () => setSelected({ region: [], year: [], rating: [] });

  const activeCount = Object.values(selected).reduce((sum, list) => sum + list.length, 0);

  if (!category) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-20 text-center md:px-6">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="mt-4 text-xl font-bold text-foreground-900">内容不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">未找到「{type}」分类，请从下方入口重新选择。</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {categories.map((item) => (
            <Link
              key={item.key}
              to={item.path}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              {item.name}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "影视分类" },
          { label: category.name },
        ]}
      />

      <header className="mt-4 rounded-lg border border-background-200 bg-gradient-to-r from-primary-700 to-primary-500 p-5 md:p-7">
        <h1 className="flex items-center gap-3 text-2xl font-bold text-background-50 md:text-3xl">
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-background-50/15">
            <i className={`${category.icon} text-2xl`}></i>
          </span>
          爱妻艺{category.name}频道
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-background-100 md:text-base">
          {category.description}
        </p>
      </header>

      <div className="mt-6">
        <FilterPanel
          category={category}
          groups={filterGroups}
          selected={selected}
          onToggle={handleToggle}
          onReset={handleReset}
          open={filtersOpen}
          onToggleOpen={() => setFiltersOpen((prev) => !prev)}
          activeCount={activeCount}
        />
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-500">
          共找到 <span className="font-semibold text-primary-600">{filteredList.length}</span> 部作品
        </p>
        <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
          {sortOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setSort(option.key)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                sort === option.key
                  ? "bg-background-50 font-medium text-primary-600 shadow-sm"
                  : "text-foreground-500 hover:text-foreground-800"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {pagedList.length > 0 ? (
        <div data-product-shop className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 md:gap-4">
          {pagedList.map((work) => (
            <WorkCard key={work.id} work={work} />
          ))}
        </div>
      ) : (
        <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-inbox-line text-3xl"></i>
          </span>
          <p className="mt-4 text-base font-medium text-foreground-800">无匹配内容，请调整筛选条件</p>
          <p className="mt-1 text-sm text-foreground-500">试试减少筛选条件，或查看全部{category.name}</p>
          <button
            type="button"
            onClick={handleReset}
            className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            清空筛选条件
          </button>
        </div>
      )}

      <Pagination current={safePage} total={totalPages} onChange={setPage} />
    </div>
  );
}